import { Button } from '@edsn/pie-components';
import { format } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Banner } from '#pie/components/banner/Banner';
import { Card } from '#pie/components/card/Card';
import { DataTable } from '#pie/components/data-table/DataTable';
import { InputSearch } from '#pie/components/input-search/InputSearch';
import { Message } from '#pie/components/message/Message';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { VerifyDataModal } from '#pie/components/verify-data-modal/VerifyDataModal';
import { useDataTable } from '#pie/hooks/useDataTable';
import {
  getCprManagementContacts,
  useGetCprManagementContacts,
} from '#pie/internal/api/pie-bff';
import { cprManagementContactSortingAtom } from '#pie/stores/cprManagementContact';
import {
  cprCloseVerificationBannerAtom,
  cprGreenVerificationAtom,
} from '#pie/stores/cprVerificationAtom';
import { searchAtom } from '#pie/stores/search';
import { emptyFilterAtom } from '#pie/stores/searchOnlyFilter';

type ContactGroupManagementProps = {
  showVerificationMessage?: boolean;
  lastVerified?: Date | null;
};

export const ContactGroupManagement = ({
  showVerificationMessage,
  lastVerified,
}: ContactGroupManagementProps) => {
  const { t } = useTranslation();
  const { t: thl } = useTranslation('hyperlinks');
  const filters = useAtomValue(emptyFilterAtom);
  const { data, isLoading } = useGetCprManagementContacts({
    pagination: { pageIndex: 0, pageSize: 100 },
    filter: filters,
  });

  const organisationId = data?.items?.[0]?.organisation
    ? data.items[0].organisation.id
    : '';

  const validationDate = lastVerified
    ? format(new Date(lastVerified), 'dd MMMM yyyy')
    : 'Onbekend';

  const tableProps = useDataTable({
    columns: (helper) => [
      helper.accessor('contactGroup.name', {
        cell: (info) => (
          <Link
            className="text-action-primary-enabled link"
            to={info.row.original.contactGroup.id}
            aria-label={info.getValue()}
            data-title={info.getValue()}
          >
            {info.getValue()}
          </Link>
        ),
        header: t('common.contact_group'),
        meta: { columnWidth: 250 },
      }),
      helper.accessor('contactGroup.isRequired', {
        cell: (info) => (info.getValue() ? t('common.yes') : t('common.no')),
        header: t('common.required'),
      }),
      helper.accessor('name', {
        header: t('common.name'),
      }),
      helper.accessor('email', {
        header: t('common.email'),
        meta: { columnWidth: 250 },
      }),
      helper.accessor('phone', {
        header: t('common.phone'),
      }),
    ],
    dateKeys: [],
    filtersAtom: emptyFilterAtom,
    query: getCprManagementContacts,
    sortingAtom: cprManagementContactSortingAtom,
  });

  const [search, setSearch] = useAtom(searchAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const verification = useAtomValue(cprGreenVerificationAtom);
  const [closeVerificationBanner, setCloseVerificationBanner] = useAtom(
    cprCloseVerificationBannerAtom,
  );

  return (
    <Stack gap="md" className="flex gap-6">
      <Text>{t('cpr_management_group.intro.part_1')}</Text>
      <Text>
        {t('cpr_management_group.intro.part_2')}{' '}
        <Link
          className="text-action-primary-enabled link"
          target="blank"
          to={thl('cpr.contact_person_register')}
        >
          {t('site_name')}
        </Link>
      </Text>
      {!isLoading &&
        (verification ? (
          <Message variant="success">
            <Text>
              <Trans
                i18nKey="cpr_management_group.quality_checkbox.message.verified_green"
                values={{ date: validationDate }}
              />
            </Text>
          </Message>
        ) : showVerificationMessage ? (
          <Message variant="error">
            <div>
              <Text>
                <Trans i18nKey="cpr_management_group.quality_checkbox.message.not_verified" />
              </Text>
              <Button
                aria-label={t('cpr_management_group.button.verify')}
                onClick={() => setIsModalOpen(true)}
                emphasis="minimal"
                className="p-0 hover:bg-transparent underline active:bg-transparent"
              >
                {t('cpr_management_group.button.verify')}
              </Button>
            </div>
          </Message>
        ) : (
          !closeVerificationBanner?.includes(organisationId) && (
            <Banner
              message={
                <Text className="text-content-info">
                  <Trans
                    i18nKey="cpr_management_group.quality_checkbox.message.verified"
                    values={{ date: validationDate }}
                  />
                </Text>
              }
              closeBanner={() =>
                setCloseVerificationBanner((prev) => [...prev, organisationId])
              }
              className="mb-0"
            />
          )
        ))}

      <VerifyDataModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        organisationId={organisationId}
        validationDate={validationDate}
        cprContacts={data ? data.items : []}
      />

      <Card className="flex-1 overflow-x-hidden p-4">
        <Stack gap="md">
          <div className="px-3" role="toolbar">
            <InputSearch
              value={search || ''}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search.placeholder')}
              onClear={() => setSearch('')}
            />
          </div>
          <DataTable
            rowLinks={(row) => ({
              'aria-label': row.original.contactGroup.name,
              to: row.original.contactGroup.id,
            })}
            {...tableProps}
            getRowId={(r) => r.id}
          />
        </Stack>
      </Card>
    </Stack>
  );
};
